'use client';

import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Anchor, Button, Heading, Text } from '@zillow/constellation-10';

import HeaderNav from '@/src/components/features/navigation/HeaderNav';
import { Link } from '@/src/i18n/navigation';
import { DefaultLayout } from '@/src/layouts/DefaultLayout';
import { actions, body, panel } from '@/src/styles/pages/errorPage.styles';
import { logNextErrorBoundary } from '../_components/errorBoundaryReporting';

type LocaleErrorBoundaryProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

/**
 * A page that throws under `/[locale]` keeps the site header and footer, as the not-found page
 * does (HP-9393). Their messages come from `[locale]/layout.tsx`. If this chrome throws too, the
 * error reaches the bare `app/error.tsx`.
 */
export default function LocaleErrorBoundary({ error, reset }: LocaleErrorBoundaryProps) {
  const t = useTranslations('errorPage');

  useEffect(() => {
    logNextErrorBoundary(error, 'locale');
  }, [error]);

  return (
    <>
      <HeaderNav />
      <DefaultLayout>
        <div className={panel}>
          <Heading level={1} textStyle="heading-lg">
            {t('heading')}
          </Heading>
          <Text asChild textStyle="body-lg" color="text.subtle">
            <p className={body}>{t('body')}</p>
          </Text>
          <div className={actions}>
            <Button tone="brand" emphasis="filled" onClick={reset}>
              {t('retry')}
            </Button>
            <Anchor asChild>
              <Link href="/">{t('home')}</Link>
            </Anchor>
          </div>
          {error.digest && (
            <Text asChild textStyle="body-sm" color="text.subtle">
              <p className={body}>{t('reference', { digest: error.digest })}</p>
            </Text>
          )}
        </div>
      </DefaultLayout>
    </>
  );
}
